import {View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {makeProfileLink} from '#/lib/routes/links'
import {niceDate} from '#/lib/strings/time'
import {atoms as a, useTheme} from '#/alf'
import * as Dialog from '#/components/Dialog'
import {InlineLinkText} from '#/components/Link'
import {Text} from '#/components/Typography'
import type {Badge} from '#/lib/badges/badgeTypes'
import {getBadgeVisual} from './badgeUtils'

export function BadgeDetailsSheet({
  control,
  badges,
}: {
  control: Dialog.DialogOuterProps['control']
  badges: Badge[]
}) {
  if (!badges.length) return null
  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <BadgeDetailsSheetInner badges={badges} control={control} />
    </Dialog.Outer>
  )
}

function BadgeDetailsSheetInner({
  control,
  badges,
}: {
  control: Dialog.DialogOuterProps['control']
  badges: Badge[]
}) {
  const t = useTheme()
  const {i18n, _} = useLingui()

  return (
    <Dialog.ScrollableInner label={_(msg`Badge details`)}>
      <View style={[a.gap_lg]}>
        {badges.map((badge, index) => {
          const {Icon, color} = getBadgeVisual(badge, t)
          const sourceLabel = badge.source === 'tellus' ? 'Tellus' : 'Bluesky'
          const issuedAt = badge.issuedAt
            ? niceDate(i18n, badge.issuedAt)
            : undefined
          const org = badge.org
          const orgLink = org
            ? makeProfileLink({
                did: org.didOrHandle,
                handle: org.didOrHandle.startsWith('did:') ? '' : org.didOrHandle,
              })
            : undefined

          return (
            <View
              key={`${badge.source}-${badge.type}-${badge.org?.id || index}`}
              style={[
                index > 0 && [a.pt_lg, a.border_t, t.atoms.border_contrast_low],
              ]}>
              <View style={[a.flex_row, a.align_center, a.gap_sm, a.mb_sm]}>
                <Icon size="lg" fill={color} />
                <Text style={[a.text_lg, a.font_heavy]}>{badge.label}</Text>
              </View>
              <View style={[a.gap_xs]}>
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  <Trans>
                    Source: <Text style={[a.font_bold]}>{sourceLabel}</Text>
                  </Trans>
                </Text>
                {badge.reason ? (
                  <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                    <Trans>
                      Reason: <Text style={[a.font_bold]}>{badge.reason}</Text>
                    </Trans>
                  </Text>
                ) : null}
                {issuedAt ? (
                  <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                    <Trans>
                      Issued: <Text style={[a.font_bold]}>{issuedAt}</Text>
                    </Trans>
                  </Text>
                ) : null}
                {org && orgLink ? (
                  <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                    <Trans>
                      Organization:{' '}
                      <InlineLinkText
                        label={org.name}
                        to={orgLink}
                        onPress={() => control.close()}>
                        {org.name}
                      </InlineLinkText>
                    </Trans>
                  </Text>
                ) : null}
              </View>
            </View>
          )
        })}
      </View>
    </Dialog.ScrollableInner>
  )
}
